'use client';

import { c as _c } from "react/compiler-runtime";
import { decodeColor, formatCents } from 'betterplace-utils';
import { AnimateNumber, ProgressBar } from 'design-system/client';
import { pick } from 'lodash';
import { useLocale } from 'next-intl';
import { type CSSProperties, useMemo } from 'react';
import { composeSelectedFontObject } from '../helpers';
import StreambotWrapper, { type StreambotWrapperProps } from '../StreambotWrapper';
import type { StreambotComponentProps } from '../types';
import WidgetHeadline from '../WidgetHeadline';
import getDefaultValues from './getDefaultValues';
import type { ProgressWidgetSettings } from './progressWidgetSettingsSchema';
import { useApiV4WithDemoData } from '@/api';
import useGoogleFonts from '@/fonts/useGoogleFonts';
import type { Maybe, NonNullableRecord, RequiredWithUndefined } from '@/types';
export type ProgressComponentData = {
  donated_amount_in_cents: number;
  requested_amount_in_cents: number | null;
  progress_percentage: number | null;
};
export type ProgressProps = StreambotComponentProps<ProgressWidgetSettings, ProgressComponentData>;
export const defaultDemoData = {
  donated_amount_in_cents: 1337,
  requested_amount_in_cents: 4200,
  progress_percentage: 31
} satisfies ProgressComponentData;
const INTERVAL_MS = 5000;
function useData(fundraisingEventId, demoData) {
  const $ = _c(3);
  const locale = useLocale();
  let t0;
  if ($[0] !== locale) {
    t0 = {
      locale
    };
    $[0] = locale;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      selector(data) {
        if (!data) {
          return null;
        }
        return pick(data, ["requested_amount_in_cents", "donated_amount_in_cents", "progress_percentage"]);
      },
      intervalMs: INTERVAL_MS
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useApiV4WithDemoData(`/fundraising_events/${fundraisingEventId}`, t0, t1, demoData);
}
type TargetProgressProps = RequiredWithUndefined<Omit<ProgressProps, 'backgroundColor' | 'textAlign' | 'textColor' | 'className' | 'fontFamily' | 'fontSize' | 'fontWeight' | 'overflow' | 'demoData' | 'withDemoData' | 'fundraisingEventId'> & NonNullableRecord<ProgressComponentData>>;
function TargetProgress(t0) {
  const $ = _c(17);
  let display;
  let progress_percentage;
  let props;
  if ($[0] !== t0) {
    ({
      display,
      progress_percentage,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = display;
    $[2] = progress_percentage;
    $[3] = props;
  } else {
    display = $[1];
    progress_percentage = $[2];
    props = $[3];
  }
  switch (display) {
    case "text":
      {
        let t1;
        if ($[4] !== props) {
          t1 = <div><TargetText {...props} /></div>;
          $[4] = props;
          $[5] = t1;
        } else {
          t1 = $[5];
        }
        return t1;
      }
    case "bar":
      {
        let t1;
        if ($[6] !== progress_percentage || $[7] !== props) {
          t1 = <div><ProgressBar {...props} progressPercentage={progress_percentage} /></div>;
          $[6] = progress_percentage;
          $[7] = props;
          $[8] = t1;
        } else {
          t1 = $[8];
        }
        return t1;
      }
    default:
      {
        let t1;
        if ($[9] !== props) {
          t1 = <TargetText {...props} />;
          $[9] = props;
          $[10] = t1;
        } else {
          t1 = $[10];
        }
        let t2;
        if ($[11] !== progress_percentage || $[12] !== props) {
          t2 = <ProgressBar {...props} progressPercentage={progress_percentage} />;
          $[11] = progress_percentage;
          $[12] = props;
          $[13] = t2;
        } else {
          t2 = $[13];
        }
        let t3;
        if ($[14] !== t1 || $[15] !== t2) {
          t3 = <div>{t1}{t2}</div>;
          $[14] = t1;
          $[15] = t2;
          $[16] = t3;
        } else {
          t3 = $[16];
        }
        return t3;
      }
  }
}
type TargetTextProps = {
  donated_amount_in_cents: number;
  requested_amount_in_cents?: Maybe<number>;
  headlineText?: string;
  headlineFont?: string;
  headlineFontColor?: string;
  headlineFontSize?: number;
  headlineFontWeight?: number;
  dataFont?: string;
  dataFontColor?: string;
  dataFontSize?: number;
  dataFontWeight?: number;
};
function TargetText(t0) {
  const $ = _c(39);
  const {
    donated_amount_in_cents,
    requested_amount_in_cents,
    headlineText,
    headlineFont,
    headlineFontColor,
    headlineFontSize,
    headlineFontWeight,
    dataFont,
    dataFontColor,
    dataFontSize,
    dataFontWeight
  } = t0;
  let t1;
  if ($[0] !== headlineFont || $[1] !== headlineFontWeight) {
    t1 = composeSelectedFontObject(headlineFont, headlineFontWeight);
    $[0] = headlineFont;
    $[1] = headlineFontWeight;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== dataFont || $[4] !== dataFontWeight) {
    t2 = composeSelectedFontObject(dataFont, dataFontWeight);
    $[3] = dataFont;
    $[4] = dataFontWeight;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = [t1, t2];
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const fonts = t3;
  let t4;
  if ($[9] !== fonts) {
    t4 = {
      fonts
    };
    $[9] = fonts;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  useGoogleFonts(t4);
  const locale = useLocale();
  let t5;
  if ($[11] !== donated_amount_in_cents || $[12] !== locale) {
    t5 = formatCents(locale, donated_amount_in_cents);
    $[11] = donated_amount_in_cents;
    $[12] = locale;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const amount = t5;
  let t6;
  if ($[14] !== locale || $[15] !== requested_amount_in_cents) {
    t6 = requested_amount_in_cents ? formatCents(locale, requested_amount_in_cents) : undefined;
    $[14] = locale;
    $[15] = requested_amount_in_cents;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const target = t6;
  let t7;
  if ($[17] !== headlineFont || $[18] !== headlineFontColor || $[19] !== headlineFontSize || $[20] !== headlineFontWeight || $[21] !== headlineText) {
    t7 = headlineText ? <WidgetHeadline content={headlineText} headlineFont={headlineFont} headlineFontColor={headlineFontColor} headlineFontSize={headlineFontSize} headlineFontWeight={headlineFontWeight} /> : null;
    $[17] = headlineFont;
    $[18] = headlineFontColor;
    $[19] = headlineFontSize;
    $[20] = headlineFontWeight;
    $[21] = headlineText;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const t8 = `${dataFontSize}px`;
  let t9;
  if ($[23] !== dataFontColor) {
    t9 = decodeColor(dataFontColor?.toString());
    $[23] = dataFontColor;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== dataFont || $[26] !== dataFontWeight || $[27] !== t8 || $[28] !== t9) {
    t10 = {
      "--streambot-progress-bar-data-font": dataFont,
      "--streambot-progress-bar-data-font-weight": dataFontWeight,
      "--streambot-progress-bar-data-font-size": t8,
      "--streambot-progress-bar-data-font-color": t9
    };
    $[25] = dataFont;
    $[26] = dataFontWeight;
    $[27] = t8;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const t11 = t10 as CSSProperties;
  let t12;
  if ($[30] !== amount) {
    t12 = <AnimateNumber animationDurationInMs={1500} maxNumberScale={1.3}>{amount}</AnimateNumber>;
    $[30] = amount;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const t13 = target ? `/${target}` : "";
  let t14;
  if ($[32] !== t11 || $[33] !== t12 || $[34] !== t13) {
    t14 = <div className="progressData" style={t11}>{t12}{t13}</div>;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t14 || $[37] !== t7) {
    t15 = <div className="progressLabel">{t7}{t14}</div>;
    $[36] = t14;
    $[37] = t7;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  return t15;
}
function NonTargetProgress(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <div><TargetText {...props} /></div>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
type ProgressContentProps = RequiredWithUndefined<Omit<ProgressProps, keyof StreambotWrapperProps>>;
function ProgressContent(t0) {
  const $ = _c(28);
  const {
    fundraisingEventId,
    withDemoData,
    demoData,
    display,
    progressBackgroundColor,
    progressColor,
    headlineFont,
    headlineFontSize,
    headlineFontWeight,
    headlineFontColor,
    headlineText,
    dataFont,
    dataFontColor,
    dataFontWeight,
    dataFontSize,
    darkMode
  } = t0;
  const {
    data
  } = useData(fundraisingEventId, withDemoData ? demoData : undefined);
  if (!data) {
    return null;
  }
  const {
    requested_amount_in_cents,
    progress_percentage,
    donated_amount_in_cents
  } = data;
  if (typeof requested_amount_in_cents !== "number") {
    let t1;
    if ($[0] !== dataFont || $[1] !== dataFontColor || $[2] !== dataFontSize || $[3] !== dataFontWeight || $[4] !== donated_amount_in_cents || $[5] !== headlineFont || $[6] !== headlineFontColor || $[7] !== headlineFontSize || $[8] !== headlineFontWeight || $[9] !== headlineText) {
      t1 = <NonTargetProgress donated_amount_in_cents={donated_amount_in_cents} headlineText={headlineText} headlineFont={headlineFont} headlineFontColor={headlineFontColor} headlineFontSize={headlineFontSize} headlineFontWeight={headlineFontWeight} dataFont={dataFont} dataFontColor={dataFontColor} dataFontSize={dataFontSize} dataFontWeight={dataFontWeight} />;
      $[0] = dataFont;
      $[1] = dataFontColor;
      $[2] = dataFontSize;
      $[3] = dataFontWeight;
      $[4] = donated_amount_in_cents;
      $[5] = headlineFont;
      $[6] = headlineFontColor;
      $[7] = headlineFontSize;
      $[8] = headlineFontWeight;
      $[9] = headlineText;
      $[10] = t1;
    } else {
      t1 = $[10];
    }
    return t1;
  }
  const t1 = progress_percentage ?? 0;
  let t2;
  if ($[11] !== darkMode || $[12] !== dataFont || $[13] !== dataFontColor || $[14] !== dataFontSize || $[15] !== dataFontWeight || $[16] !== display || $[17] !== donated_amount_in_cents || $[18] !== headlineFont || $[19] !== headlineFontColor || $[20] !== headlineFontSize || $[21] !== headlineFontWeight || $[22] !== headlineText || $[23] !== progressBackgroundColor || $[24] !== progressColor || $[25] !== requested_amount_in_cents || $[26] !== t1) {
    t2 = <TargetProgress display={display} progressBackgroundColor={progressBackgroundColor} progressColor={progressColor} donated_amount_in_cents={donated_amount_in_cents} requested_amount_in_cents={requested_amount_in_cents} progress_percentage={t1} headlineFont={headlineFont} headlineFontSize={headlineFontSize} headlineFontWeight={headlineFontWeight} headlineFontColor={headlineFontColor} headlineText={headlineText} dataFont={dataFont} dataFontColor={dataFontColor} dataFontWeight={dataFontWeight} dataFontSize={dataFontSize} darkMode={darkMode} />;
    $[11] = darkMode;
    $[12] = dataFont;
    $[13] = dataFontColor;
    $[14] = dataFontSize;
    $[15] = dataFontWeight;
    $[16] = display;
    $[17] = donated_amount_in_cents;
    $[18] = headlineFont;
    $[19] = headlineFontColor;
    $[20] = headlineFontSize;
    $[21] = headlineFontWeight;
    $[22] = headlineText;
    $[23] = progressBackgroundColor;
    $[24] = progressColor;
    $[25] = requested_amount_in_cents;
    $[26] = t1;
    $[27] = t2;
  } else {
    t2 = $[27];
  }
  return t2;
}
function Progress(t0) {
  const $ = _c(42);
  let className;
  let demoData;
  let fundraisingEventId;
  let t1;
  let withDemoData;
  if ($[0] !== t0) {
    const {
      fundraisingEventId: t2,
      className: t3,
      withDemoData: t4,
      demoData: t5,
      ...props
    } = t0;
    fundraisingEventId = t2;
    className = t3;
    withDemoData = t4;
    demoData = t5 === undefined ? defaultDemoData : t5;
    t1 = getDefaultValues(props);
    $[0] = t0;
    $[1] = className;
    $[2] = demoData;
    $[3] = fundraisingEventId;
    $[4] = t1;
    $[5] = withDemoData;
  } else {
    className = $[1];
    demoData = $[2];
    fundraisingEventId = $[3];
    t1 = $[4];
    withDemoData = $[5];
  }
  const merged = t1;
  let darkMode;
  let dataFont;
  let dataFontColor;
  let dataFontSize;
  let dataFontWeight;
  let display;
  let headlineFont;
  let headlineFontColor;
  let headlineFontSize;
  let headlineFontWeight;
  let headlineText;
  let progressBackgroundColor;
  let progressColor;
  let wrapperProps;
  if ($[6] !== merged) {
    ({
      display,
      progressBackgroundColor,
      progressColor,
      headlineFont,
      headlineFontSize,
      headlineFontWeight,
      headlineFontColor,
      headlineText,
      dataFont,
      dataFontColor,
      dataFontWeight,
      dataFontSize,
      darkMode,
      ...wrapperProps
    } = merged);
    $[6] = merged;
    $[7] = darkMode;
    $[8] = dataFont;
    $[9] = dataFontColor;
    $[10] = dataFontSize;
    $[11] = dataFontWeight;
    $[12] = display;
    $[13] = headlineFont;
    $[14] = headlineFontColor;
    $[15] = headlineFontSize;
    $[16] = headlineFontWeight;
    $[17] = headlineText;
    $[18] = progressBackgroundColor;
    $[19] = progressColor;
    $[20] = wrapperProps;
  } else {
    darkMode = $[7];
    dataFont = $[8];
    dataFontColor = $[9];
    dataFontSize = $[10];
    dataFontWeight = $[11];
    display = $[12];
    headlineFont = $[13];
    headlineFontColor = $[14];
    headlineFontSize = $[15];
    headlineFontWeight = $[16];
    headlineText = $[17];
    progressBackgroundColor = $[18];
    progressColor = $[19];
    wrapperProps = $[20];
  }
  let t2;
  if ($[21] !== darkMode || $[22] !== dataFont || $[23] !== dataFontColor || $[24] !== dataFontSize || $[25] !== dataFontWeight || $[26] !== demoData || $[27] !== display || $[28] !== fundraisingEventId || $[29] !== headlineFont || $[30] !== headlineFontColor || $[31] !== headlineFontSize || $[32] !== headlineFontWeight || $[33] !== headlineText || $[34] !== progressBackgroundColor || $[35] !== progressColor || $[36] !== withDemoData) {
    t2 = <ProgressContent fundraisingEventId={fundraisingEventId} progressBackgroundColor={progressBackgroundColor} progressColor={progressColor} withDemoData={withDemoData} display={display} demoData={demoData} headlineFont={headlineFont} headlineFontSize={headlineFontSize} headlineFontWeight={headlineFontWeight} headlineFontColor={headlineFontColor} headlineText={headlineText} dataFont={dataFont} dataFontColor={dataFontColor} dataFontWeight={dataFontWeight} dataFontSize={dataFontSize} darkMode={darkMode} />;
    $[21] = darkMode;
    $[22] = dataFont;
    $[23] = dataFontColor;
    $[24] = dataFontSize;
    $[25] = dataFontWeight;
    $[26] = demoData;
    $[27] = display;
    $[28] = fundraisingEventId;
    $[29] = headlineFont;
    $[30] = headlineFontColor;
    $[31] = headlineFontSize;
    $[32] = headlineFontWeight;
    $[33] = headlineText;
    $[34] = progressBackgroundColor;
    $[35] = progressColor;
    $[36] = withDemoData;
    $[37] = t2;
  } else {
    t2 = $[37];
  }
  let t3;
  if ($[38] !== className || $[39] !== t2 || $[40] !== wrapperProps) {
    t3 = <StreambotWrapper {...wrapperProps} className={className}>{t2}</StreambotWrapper>;
    $[38] = className;
    $[39] = t2;
    $[40] = wrapperProps;
    $[41] = t3;
  } else {
    t3 = $[41];
  }
  return t3;
}
export default Progress;